/**
 * Native push (Capacitor / FCM) — the Android app's push provider.
 *
 * Why this exists alongside services/firebase.ts and utils/iosPush.ts:
 *   - utils/iosPush.ts   -> VAPID Web Push. The provider for real BROWSERS.
 *   - services/firebase.ts -> FCM Web SDK. A browser fallback that has never
 *     been reachable in prod (no REACT_APP_FIREBASE_* at build time) — left
 *     alone deliberately; see docs/operations/structural-debt-backlog.md.
 *   - this file          -> FCM via the native Capacitor bridge. The only
 *     provider that can deliver to the Android app when it is fully closed.
 *
 * VAPID Web Push is NOT a substitute inside the native shell: the Android
 * System WebView does not implement the Push API, so a WebView-hosted page can
 * appear to subscribe and then silently never receive anything. That is why
 * the native branch must be tried BEFORE VAPID in PushNotificationContext.
 *
 * The plugin is loaded via dynamic import throughout so that browser bundles
 * never pull it in — every export here is a no-op off-native.
 */

import { Capacitor } from '@capacitor/core';

/** localStorage key holding the FCM registration token we last sent to the API. */
export const NATIVE_PUSH_TOKEN_KEY = 'nativePushToken';

/** How long to wait for the FCM `registration` event before giving up. */
const REGISTRATION_TIMEOUT_MS = 15000;

/**
 * Must match `default_notification_channel_id` in
 * mobile/android/app/src/main/res/values/strings.xml, which AndroidManifest.xml
 * points FCM at via the
 * com.google.firebase.messaging.default_notification_channel_id meta-data.
 */
const DEFAULT_CHANNEL_ID = 'helm_default';

export interface NativePushPayload {
  title?: string;
  body?: string;
  data?: Record<string, unknown>;
}

/** True only inside the Capacitor native shell (Android/iOS app), not a browser. */
export function isNativePushPlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // Capacitor throws in some SSR/test environments rather than returning false.
    return false;
  }
}

/**
 * Platform string for the device-token API. Never hardcode it: the same
 * registration code runs in the iOS shell, and a token stored under the wrong
 * platform is routed (and pruned) wrongly. Off-native or on an unknown platform
 * it falls back to 'android', the only shell with a working native push path.
 */
export function getNativePlatform(): 'ios' | 'android' {
  try {
    return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
  } catch {
    return 'android';
  }
}

/**
 * Create the default notification channel (Android 8+).
 *
 * Declaring the channel id in the manifest tells FCM which channel to target,
 * but does NOT create it — Android silently DROPS any notification whose
 * channel does not exist. Creating it is idempotent; repeat calls with the same
 * id are ignored by the OS, and a user who has since customised the channel
 * keeps their settings (Android will not let an app override those).
 *
 * No-op on iOS, which has no channel concept.
 */
export async function ensureNativePushChannel(): Promise<void> {
  if (!isNativePushPlatform()) return;
  try {
    if (Capacitor.getPlatform() !== 'android') return;
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.createChannel({
      id: DEFAULT_CHANNEL_ID,
      name: 'Helm notifications',
      description: 'Check-ins, bond updates and messages',
      importance: 4, // HIGH — heads-up display; matches user expectation for DMs
      vibration: true,
      // NOTE: `visibility` is deliberately NOT set here. Passing visibility: 0
      // (PRIVATE) was verified on an Android 15 emulator to have NO effect — the
      // created channel still reports mLockscreenVisibility=-1000
      // (VISIBILITY_NO_OVERRIDE), i.e. the channel defers to each notification.
      // Lock-screen content is private in practice because the notifications
      // themselves default to vis=PRIVATE (confirmed in dumpsys), NOT because of
      // anything set here. Do not re-add a visibility argument and assume it
      // hardens lock-screen privacy — it does not. Helm carries relationship and
      // scene context, so if that guarantee is ever needed it must be enforced
      // on the notification payload, and verified in dumpsys.
    });
  } catch {
    // Non-fatal: a failed channel creation must not block registration. FCM
    // falls back to its auto-created channel, which still delivers.
  }
}

/**
 * Request permission and obtain an FCM registration token.
 *
 * The token does NOT come back from register() — it arrives asynchronously on
 * the 'registration' listener, which is the part that is easy to get wrong.
 * Resolves null on denial, on registrationError, or on timeout.
 */
export async function registerNativePush(): Promise<string | null> {
  if (!isNativePushPlatform()) return null;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  // Channel must exist before the first notification arrives, not merely before
  // the user opts in — FCM can deliver as soon as the token is registered.
  await ensureNativePushChannel();

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== 'granted') return null;

  return new Promise<string | null>((resolve) => {
    let settled = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];

    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Detach both listeners; a leaked 'registration' handler would resolve a
      // dead promise on every subsequent token rotation.
      handles.forEach((h) => {
        h.remove().catch(() => {});
      });
      resolve(value);
    };

    const timer = setTimeout(() => finish(null), REGISTRATION_TIMEOUT_MS);

    // Both listeners must be attached BEFORE register() is called. addListener
    // crosses the native bridge asynchronously; calling register() without
    // awaiting it lets the 'registration' event fire into no listener, and the
    // promise then waits out the full timeout and resolves null.
    (async () => {
      try {
        handles.push(await PushNotifications.addListener('registration', (token) => finish(token.value)));
        handles.push(await PushNotifications.addListener('registrationError', () => finish(null)));
        if (settled) {
          // finish() ran while we were still attaching (timeout): drop what we added.
          handles.forEach((h) => {
            h.remove().catch(() => {});
          });
          return;
        }
        await PushNotifications.register();
      } catch {
        finish(null);
      }
    })();
  });
}

/**
 * Attach foreground-delivery and notification-tap handlers.
 * Returns an unsubscribe function, or null off-native.
 */
export async function addNativePushListeners(
  onReceived: (payload: NativePushPayload) => void,
  onAction: (payload: NativePushPayload) => void
): Promise<(() => void) | null> {
  if (!isNativePushPlatform()) return null;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  const received = await PushNotifications.addListener('pushNotificationReceived', (n) => {
    onReceived({ title: n.title, body: n.body, data: n.data });
  });
  const actioned = await PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
    onAction({
      title: a.notification.title,
      body: a.notification.body,
      data: a.notification.data,
    });
  });

  return () => {
    received.remove().catch(() => {});
    actioned.remove().catch(() => {});
  };
}

/** Remove all native listeners and drop the OS-level registration. */
export async function unregisterNativePush(): Promise<void> {
  if (!isNativePushPlatform()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllListeners();
  } catch {
    // Non-fatal: the caller still clears the stored token and tells the API.
  }
}
